// ChangePasswordDialog.tsx — modal form for changing the active account's password.
// Persists via useSession().updatePassword(currentPassword, newPassword).
//
// The current password is required by the API (1.23.80) and therefore asked for here.
// Every other account-takeover surface on this product already demands it inside a
// live session — recovery email, phone, 2FA enable/disable, appeals — and changing
// the password itself was the one exemption, which made a borrowed laptop or a
// stolen session cookie enough to take an account permanently.

import { useState } from 'react';
import type { ChangeEvent } from 'react';
import styled from 'styled-components';
import { Dialog, TextField, Button, useToast } from '@/ui';
import { useSession } from '@/hooks';
import { InvalidCredentials, RateLimited } from '@/core/services/authService';

// ─── Styled components ─────────────────────────────────────────────────────────

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

// ─── Component ─────────────────────────────────────────────────────────────────

export function ChangePasswordDialog({ onClose }: { onClose: () => void }) {
  const { updatePassword } = useSession();
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [currentError, setCurrentError] = useState('');

  async function handleSave() {
    if (!current) {
      setCurrentError('Enter your current password.');
      return;
    }
    if (next.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (next !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    try {
      const ok = await updatePassword(current, next);
      if (!ok) {
        setError('Could not update password. Please try again.');
        return;
      }
    } catch (e) {
      // Name the field at fault rather than collapsing every failure into one
      // message — a member who mistyped their current password has no way to tell
      // that apart from a server problem otherwise.
      if (e instanceof InvalidCredentials) {
        setCurrentError('That is not your current password.');
        return;
      }
      if (e instanceof RateLimited) {
        setError('Too many attempts. Please wait a moment and try again.');
        return;
      }
      setError('Could not update password. Please try again.');
      return;
    }
    toast('Password updated. Any other devices signed in to this account have been signed out.');
    onClose();
  }

  return (
    <Dialog open title="Change Password" onClose={onClose}>
      <Stack>
        <TextField
          label="Current password"
          type="password"
          autoComplete="current-password"
          value={current}
          error={currentError || undefined}
          onChange={(e: ChangeEvent<HTMLInputElement>) => { setCurrent(e.currentTarget.value); setCurrentError(''); }}
        />
        <TextField
          label="New password"
          type="password"
          autoComplete="new-password"
          value={next}
          error={error && next.length < 8 ? error : undefined}
          onChange={(e: ChangeEvent<HTMLInputElement>) => { setNext(e.currentTarget.value); setError(''); }}
        />
        <TextField
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          error={error && next.length >= 8 ? error : undefined}
          onChange={(e: ChangeEvent<HTMLInputElement>) => { setConfirm(e.currentTarget.value); setError(''); }}
        />
        <Row>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={() => void handleSave()}>Update Password</Button>
        </Row>
      </Stack>
    </Dialog>
  );
}
